import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../hooks/use-user';
import useApiRequest from '../../lib/hooks/useRequest';
import { UserRole } from '../../lib/interfaces/user';
import { Button } from '../../ui/components/ui/button';
import KpiCacCard from './components/kpi-cac-card/kpi-cac-card.wrapper';
import KpiDetailCard from './components/kpi-detail-card/kpi-detail-card.wrapper';
import KpiWeekSelectorWrapper from './components/kpi-week-selector/kpi-week-selector.wrapper';
import { RangeEditState, RangeFieldState } from './components/metric-row/metric-row';
import MetricSection, { MetricSectionSkeleton } from './components/metric-section/metric-section';
import { DASHBOARD_KEY } from './constants/kpi.constants';
import { RangeValueRequestDto, SaveMinMaxValuesRequestDto, SaveValuesRequestDto } from './types/request';
import { DashboardResponseDto, KpiDashboardMetaResponseDto, RangeValueType } from './types/response';

interface KpiDashboardPageProps {
  kpiDashboardMeta: KpiDashboardMetaResponseDto;
}

function splitMetricPath(metricPath: string): { sectionKey: string; cardKey: string; metricKey: string } {
  const [sectionKey, cardKey, metricKey] = metricPath.split('.');
  return { sectionKey, cardKey, metricKey };
}

function parseOptionalNumberString(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const normalized = trimmed.replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function mapRangeFieldToRequest(field: RangeFieldState): RangeValueRequestDto {
  if (field.type === RangeValueType.CALCULATED) {
    return { type: RangeValueType.CALCULATED, value: null, calculationFormula: field.formula! };
  }
  return { type: RangeValueType.STATIC, value: parseOptionalNumberString(field.value) };
}

function buildQueryString(searchParams: URLSearchParams): string {
  const params = new URLSearchParams();
  const year = searchParams.get('year');
  const week = searchParams.get('week');
  if (year) params.set('year', year);
  if (week) params.set('week', week);
  return params.toString() ? `?${params.toString()}` : '';
}

const KpiDashboardPage = ({ kpiDashboardMeta }: KpiDashboardPageProps) => {
  const { user } = useUser();
  const canEdit = user?.userRole === UserRole.SuperAdmin;

  const [searchParams] = useSearchParams();
  const { apiRequest } = useApiRequest();

  const [isLoading, setIsLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardResponseDto>();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [editedRanges, setEditedRanges] = useState<Record<string, RangeEditState>>({});

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQueryString(searchParams);
      const response = await apiRequest<DashboardResponseDto>(`kpi-dashboards/${DASHBOARD_KEY}${query}`, 'GET');
      if (response.data) setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching KPI dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, searchParams]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    setIsEditMode(false);
    setEditedValues({});
    setEditedRanges({});
  }, [searchParams]);

  const handleValueChange = (metricPath: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [metricPath]: value }));
  };

  const handleRangeChange = (metricPath: string, rangeState: RangeEditState) => {
    setEditedRanges((prev) => ({ ...prev, [metricPath]: rangeState }));
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      handleSave();
    } else {
      setIsEditMode(true);
    }
  };

  const handleCancel = () => {
    setEditedValues({});
    setEditedRanges({});
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const query = buildQueryString(searchParams);
      const promises: Promise<unknown>[] = [];

      const valueEntries = Object.entries(editedValues);
      if (valueEntries.length > 0) {
        const valuesDto: SaveValuesRequestDto = {
          metrics: valueEntries.map(([path, value]) => ({
            ...splitMetricPath(path),
            value: parseOptionalNumberString(value)
          }))
        };
        promises.push(apiRequest(`kpi-dashboards/${DASHBOARD_KEY}${query}`, 'POST', { body: valuesDto }));
      }

      const rangeEntries = Object.entries(editedRanges);
      if (rangeEntries.length > 0) {
        const minMaxDto: SaveMinMaxValuesRequestDto = {
          metrics: rangeEntries.map(([path, range]) => ({
            ...splitMetricPath(path),
            min: mapRangeFieldToRequest(range.min),
            max: mapRangeFieldToRequest(range.max)
          }))
        };
        promises.push(apiRequest(`kpi-dashboards/${DASHBOARD_KEY}/min-max${query}`, 'POST', { body: minMaxDto }));
      }

      await Promise.all(promises);
      setEditedValues({});
      setEditedRanges({});
      setIsEditMode(false);
      await fetchDashboard();
    } catch (error) {
      console.error('Error saving KPI dashboard:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 h-full">
      <div className="flex justify-between">
        <div>
          <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">{kpiDashboardMeta.title}</h1>
          <p className="text-[16px] text-[#888]">{kpiDashboardMeta.description}</p>
        </div>
        <div className="flex items-center gap-5">
          {canEdit && (
            <div className="flex items-center gap-2">
              {isEditMode && (
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Abbrechen
                </Button>
              )}
              <Button onClick={handleEditToggle} disabled={isSaving || isLoading}>
                {isSaving ? 'Speichert...' : isEditMode ? 'Speichern' : 'Werte bearbeiten'}
              </Button>
            </div>
          )}
          <KpiWeekSelectorWrapper
            years={kpiDashboardMeta.years}
            currentYear={kpiDashboardMeta.currentYear}
            currentWeek={kpiDashboardMeta.currentWeek}
            disabled={isLoading || isEditMode}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3 items-stretch">
        <div className="flex gap-3 w-full">
          <div className="w-1/3">
            <KpiCacCard meta={kpiDashboardMeta} className="h-full" />
          </div>
          <div className="w-2/3">
            <KpiDetailCard meta={kpiDashboardMeta} className="h-full" />
          </div>
        </div>
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: kpiDashboardMeta.sections.length }).map((_, i) => (
              <MetricSectionSkeleton key={i} cardCount={kpiDashboardMeta.sections[i].cardCount} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {dashboard?.sections.map((section) => (
              <MetricSection
                key={section.sectionKey}
                section={section}
                isEditMode={isEditMode}
                editedValues={editedValues}
                editedRanges={editedRanges}
                onValueChange={handleValueChange}
                onRangeChange={handleRangeChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiDashboardPage;
